import React, {useState, useEffect} from 'react';
import { Text, TouchableOpacity, View, StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import I18n from 'i18n-js';
import useWidth from '../scanToCartScanning/useWidth';
import { ButtonWithTooltip, GradientButton } from './shared/PromptComponents';
import { useDispatch, useSelector } from 'react-redux';
import { handleReScanNotFulfilledItems, handleItemOutOfStock, handleLeaveOrderAsServiceIssue, handleBackorderItem } from '../../actions/scanToCartActions';
import { sharedPromptStyles, basePromptContainer, baseResponseView, baseTitleText } from './shared/promptStyles';
const NotFulfilledItemsPrompt = ({
  items = [],
  onClose
}) => {
  const windowWidth = useWidth();
  const isDesktop = (windowWidth || 0) >= 900;
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('main');
  const [holdError, setHoldError] = useState(null);
  const [backorderError, setBackorderError] = useState(null);

  const { nextPickItem, activeCart,lastCartInput } = useSelector(state => ({
    nextPickItem: state.scanToCart.nextPickItem,
    activeCart: state.scanToCart.activeCart,
    lastCartInput: state.scanToCartScanning.lastCartInput
  }));
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltips = activeTooltip ? { [`${activeTooltip}Tooltip`]: true } : {};

  const handleClosePrompt = () => {
    onClose && onClose();
  };

  const toggleTooltip = tooltipName => {
    setActiveTooltip(prev => (prev === tooltipName ? null : tooltipName));
  };

  useEffect(() => {
    const handleClickOutside = () => setActiveTooltip(null);

    if (activeTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeTooltip]);

  const dispatch = useDispatch();

  const handleScanItems = () => {
    dispatch(handleReScanNotFulfilledItems());
    handleClosePrompt();
  };

  const handleRemoveItems = () => {
    setLoading(true);
    const data = {
      tote_id: lastCartInput,
      option: 'cancel_item',
      cart_id: activeCart?.cart_id
    };

    dispatch(handleItemOutOfStock(data, () => {
      setLoading(false);
      handleClosePrompt();
    }, nextPickItem?.item?.product_id));
  };

  const handleBackorder = () => {
    setLoading(true);
    setBackorderError(null);
    dispatch(handleBackorderItem(lastCartInput, handleClosePrompt))
      .then(result => {
        if (!result?.ok) {
          setLoading(false);
          setBackorderError(result?.error);
        }
      });
  };

  const handleServiceIssue = () => {
    setHoldError(null);
    setBackorderError(null);
    setStep('leaveChoice');
  };

  const handleBack = () => {
    setHoldError(null);
    setStep('main');
  };

  const handleHoldChoice = holdMode => {
    setLoading(true);
    setHoldError(null);
    dispatch(handleLeaveOrderAsServiceIssue(lastCartInput, holdMode, handleClosePrompt))
      .then(result => {
        if (!result?.ok) {
          setLoading(false);
          setHoldError(result?.error);
        }
      });
  };

  const renderItemsList = () => (
    <View style={styles.itemsListContainer}>
      <Text style={[isDesktop ? styles.titleText : styles.titleTextMobile]}>
        {I18n.t('notFulfilledItems.title')}
      </Text>
      <ScrollView style={[isDesktop ? styles.itemsScrollView : styles.itemsScrollViewMobile]} showsVerticalScrollIndicator={false}>
        {items.map((item, index) => (
          <Text key={index} style={[isDesktop ? styles.itemText : styles.itemTextMobile]}>
            • {item}
          </Text>
        ))}
      </ScrollView>
    </View>
  );

  const getButtonConfig = () => [
    {
      type: 'scan',
      onPress: handleScanItems,
      textKey: 'notFulfilledItems.scanButton',
      tooltipKey: 'notFulfilledItems.scanTooltip'
    },
    {
      type: 'remove',
      onPress: handleRemoveItems,
      textKey: 'notFulfilledItems.removeButton',
      tooltipKey: 'notFulfilledItems.removeTooltip'
    },
    {
      type: 'backorder',
      onPress: handleBackorder,
      textKey: 'notFulfilledItems.backorderButton',
      tooltipKey: 'notFulfilledItems.backorderTooltip'
    },
    {
      type: 'service',
      onPress: handleServiceIssue,
      textKey: 'notFulfilledItems.serviceButton',
      tooltipKey: 'notFulfilledItems.serviceTooltip'
    }
  ];

  const getHoldButtonConfig = () => [
    {
      type: 'putAway',
      onPress: () => handleHoldChoice('put_away'),
      textKey: 'notFulfilledItems.putAwayButton',
      tooltipKey: 'notFulfilledItems.putAwayTooltip'
    },
    {
      type: 'store',
      onPress: () => handleHoldChoice('store'),
      textKey: 'notFulfilledItems.storeButton',
      tooltipKey: 'notFulfilledItems.storeTooltip'
    }
  ];

  return (
    <View style={styles.responseView}>
      <LinearGradient
        locations={[0, 1]}
        start={{ x: 0, y: 1 }}
        end={{ x: 0, y: 0 }}
        colors={['#d47d02', '#ffb636']}
        style={[isDesktop ? styles.promptContainer : styles.promptContainerMobile]}>

        <View style={styles.cancelIcon}>
          <TouchableOpacity testID="CancelIcon" onPress={handleClosePrompt}>
            <i className="icon-cancel" style={{ fontSize: 26, color: '#1C1C1C' }}></i>
          </TouchableOpacity>
        </View>

        {renderItemsList()}

        {step === 'leaveChoice' && holdError && (
          <Text testID="HoldErrorMessage" style={styles.holdErrorText}>{holdError}</Text>
        )}

        {step === 'main' && backorderError && (
          <Text testID="BackorderErrorMessage" style={styles.holdErrorText}>{backorderError}</Text>
        )}

        <View style={[isDesktop ? styles.buttonContainer : styles.buttonContainerMobile]}>
          {loading ? (
            <ActivityIndicator size="large" color="#1C1C1C" />
          ) : step === 'leaveChoice' ? (
            <>
              {getHoldButtonConfig().map((button, index) => (
                <ButtonWithTooltip
                  key={index}
                  type={button.type}
                  onPress={button.onPress}
                  buttonText={I18n.t(button.textKey)}
                  tooltipText={I18n.t(button.tooltipKey)}
                  tooltips={tooltips}
                  toggleTooltip={toggleTooltip}
                />
              ))}
              <GradientButton
                testID="backButton"
                text={I18n.t('notFulfilledItems.backButton')}
                onPress={handleBack}
              />
            </>
          ) : (
            <>
              {getButtonConfig().map((button, index) => (
                <ButtonWithTooltip
                  key={index}
                  type={button.type}
                  onPress={button.onPress}
                  buttonText={I18n.t(button.textKey)}
                  tooltipText={I18n.t(button.tooltipKey)}
                  tooltips={tooltips}
                  toggleTooltip={toggleTooltip}
                />
              ))}
            </>
          )}
        </View>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  responseView: {
    ...baseResponseView,
    justifyContent: 'center',
    top: 0,
    zIndex: 99999,
  },
  promptContainer: {
    ...basePromptContainer,
    maxWidth: 600,
    maxHeight: '80%',
  },
  promptContainerMobile: {
    ...basePromptContainer,
    paddingHorizontal: 15,
    paddingVertical: 12,
    maxWidth: 400,
    maxHeight: '85%',
    marginTop: '10%',
  },
  cancelIcon: sharedPromptStyles.cancelIcon,
  itemsListContainer: {
    marginBottom: 20,
    marginTop: 10
  },
  titleText: {
    ...baseTitleText,
    marginBottom: 15
  },
  titleTextMobile: {
    ...baseTitleText,
    fontSize: 18,
    marginBottom: 12
  },
  itemsScrollView: {
    maxHeight: 200,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 5,
    padding: 10
  },
  itemsScrollViewMobile: {
    // Shrunk from the desktop 200: the main step now stacks 4 buttons, and the
    // button stack below has no scroll of its own (GROOV-5258, 375px layout check).
    maxHeight: 130,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 5,
    padding: 10
  },
  itemText: {
    fontFamily: 'Poppins_300Light',
    fontSize: 16,
    color: '#1C1C1C',
    marginBottom: 8,
    lineHeight: 22
  },
  itemTextMobile: {
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    color: '#1C1C1C',
    marginBottom: 6,
    lineHeight: 20
  },
  buttonContainer: sharedPromptStyles.buttonContainer,
  buttonContainerMobile: {
    ...sharedPromptStyles.buttonContainerMobile,
    gap: 8
  },
  holdErrorText: {
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    color: '#7a2828',
    textAlign: 'center',
    marginBottom: 10
  }
});

export default NotFulfilledItemsPrompt;
